<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue';
import type { Member } from '@ilteducation/content-collections-types';
import type { User, Group } from '@ilteducation/customer-mgmt';
import useLocale from '../helpers/use-locale';
import { useDisplay } from 'vuetify';

type Props = {
  title: string;
  participators: Member[];
  canAdd: boolean;
  canRemove?: boolean;
  canRemoveAll?: boolean;
  isLoading?: boolean;
  rounded?: 'sm' | 'default' | 'lg';
  density?: 'comfortable' | 'compact' | 'default';
  fetchUsers: (ids: string[], membersToShow?: number) => Promise<User[]>;
  fetchGroups: (ids: string[], membersToShow?: number) => Promise<Group[]>;
};

type Emits = {
  (e: 'onClickAdd'): void;
  (e: 'onClickRemove', id: string): void;
  (e: 'onClickRemoveAll'): void;
};
const emit = defineEmits<Emits>();

const defaultShowedMembersInList = 20;
const props = withDefaults(defineProps<Props>(), {
  canRemove: true,
  canRemoveAll: false,
  isLoading: false,
  rounded: 'default',
  density: 'default',
});

const { t } = useLocale();
const { platform, smAndDown, mdAndUp } = useDisplay();
const { title, participators, canAdd, canRemove } = toRefs(props);
const membersToShow = ref(defaultShowedMembersInList);
const isViewMoreActive = computed(() => membersToShow.value !== defaultShowedMembersInList);
const fetchedUsers = ref<User[]>([]);
const fetchedGroups = ref<Group[]>([]);
const activeMembers = ref([] as number[]);

type DisplayParticipant = {
  uid: string;
  name: string;
  type: 'user' | 'group';
  isUnavailable: boolean;
};

const isTouchDevice = computed(() => platform.value.touch);
const visibleMembers = computed(() => participators.value?.slice(0, membersToShow.value));
const membersInfoLoading = ref(false);

const loading = computed(() => props.isLoading || membersInfoLoading.value);

const fetchUserInformation = async () => {
  membersInfoLoading.value = true;
  const userIds = visibleMembers.value.filter((m) => m.type === 'user').map((m) => m.id);
  const groupIds = visibleMembers.value.filter((m) => m.type === 'group').map((m) => m.id);
  fetchedUsers.value = userIds.length > 0 ? await props.fetchUsers(userIds, membersToShow.value) : [];
  fetchedGroups.value = groupIds.length > 0 ? await props.fetchGroups(groupIds, membersToShow.value) : [];
  membersInfoLoading.value = false;
};

const toggleViewMore = () => {
  if (isViewMoreActive.value) {
    membersToShow.value = defaultShowedMembersInList;
  } else {
    membersToShow.value = participators.value?.length;
  }
};
watch(
  visibleMembers,
  () => {
    fetchUserInformation();
  },
  {
    immediate: true,
  },
);

watch(
  () => participators.value.length,
  () => {
    membersToShow.value = defaultShowedMembersInList;
  },
);

const displayParticipants = computed<DisplayParticipant[]>(() => {
  if (loading.value) return [];

  return visibleMembers.value
    .map((participator) => {
      const found =
        participator.type === 'user'
          ? fetchedUsers.value.find((u) => u.uid === participator.id)
          : fetchedGroups.value.find((g) => g.uid === participator.id);

      return {
        uid: found?.uid ?? participator.id,
        name:
          found?.name ??
          (participator.type === 'user' ? t('components.unavailable_member') : t('components.unavailable_group')),
        type: participator.type as 'user' | 'group',
        isUnavailable: !found,
      };
    })
    .sort((a, b) => {
      if (a.isUnavailable !== b.isUnavailable) return a.isUnavailable ? 1 : -1;
      return a.name.localeCompare(b.name);
    });
});

const activateTooltip = (index: number) => {
  activeMembers.value = [index];
};
const deactivateTooltip = () => {
  activeMembers.value = [];
};

const removeMember = (uid: string) => {
  emit('onClickRemove', uid);
  deactivateTooltip();
};
</script>

<template>
  <div>
    <div class="d-flex justify-space-between align-center">
      <p class="font-weight-bold">{{ title }} ({{ participators.length ?? 0 }})</p>
      <div>
        <v-btn
          v-if="canAdd && canRemoveAll && participators.length > 0"
          variant="text"
          size="48"
          :loading="loading"
          :aria-label="t('remove_all_members')"
          @click="emit('onClickRemoveAll')"
          color="on-surface"
        >
          <v-icon size="large">mdi-delete</v-icon>
          <v-tooltip :disabled="isTouchDevice" activator="parent" :text="t('remove_all_members')" location="top" />
        </v-btn>
        <v-btn
          v-if="canAdd"
          variant="text"
          size="48"
          :aria-label="`${t('add')} ${title}`"
          @click="emit('onClickAdd')"
          color="on-surface"
        >
          <v-icon size="large">mdi-account-plus</v-icon>
          <v-tooltip :disabled="isTouchDevice" activator="parent" :text="`${t('add')} ${title}`" location="top" />
        </v-btn>
      </div>
    </div>
    <v-divider :thickness="2" color="outline-variant" :class="['border-opacity-75', { 'mb-2': mdAndUp }]"></v-divider>
    <ul
      v-if="displayParticipants.length > 0"
      :class="['d-flex flex-wrap members-list pa-0 ga-2', { 'pt-3 pb-3': smAndDown }]"
      :aria-label="title"
    >
      <li v-for="(member, index) in displayParticipants" :key="member.uid">
        <v-hover>
          <template #default="{ isHovering, props }">
            <v-btn
              v-if="canRemove"
              v-bind="props"
              closable
              :density="density"
              variant="outlined"
              append-icon="mdi-close"
              color="on-surface"
              :rounded="rounded === 'default' ? 'true' : rounded"
              :class="`text-none ${isHovering ? 'bg-error-container' : 'bg-surface-container-low'}`"
              :aria-label="`${t('components.remove_member')} ${member.name}`"
              @click="removeMember(member.uid)"
              @focus="activateTooltip(index)"
              @blur="deactivateTooltip()"
              @mouseenter="activateTooltip(index)"
              @mouseleave="deactivateTooltip()"
            >
              <template #prepend>
                <v-icon
                  :color="member.isUnavailable ? 'error' : isHovering ? 'on-error-container' : 'primary'"
                  start
                  :icon="member.type === 'user' ? 'mdi-account' : 'mdi-account-supervisor'"
                />
              </template>

              <span :class="member.isUnavailable ? 'text-error' : 'text-on-surface'">{{ member.name }}</span>

              <v-tooltip
                :disabled="isTouchDevice"
                activator="parent"
                :class="{ 'hidden-tooltip': !activeMembers.includes(index) }"
                location="top"
                :text="`${t('components.remove_member')} ${member.name}`"
              />
            </v-btn>
            <v-btn
              v-else
              label
              color="on-surface"
              variant="outlined"
              :density="density"
              :rounded="rounded === 'default' ? 'true' : rounded"
              :class="member.isUnavailable ? 'bg-error-container' : 'bg-surface-container-low'"
              readonly
            >
              <v-icon
                start
                :color="member.isUnavailable ? 'error' : 'primary'"
                :icon="member.type === 'user' ? 'mdi-account' : 'mdi-account-supervisor'"
              />
              <span :class="member.isUnavailable ? 'text-error' : 'text-on-surface'">{{ member.name }}</span>
            </v-btn>
          </template>
        </v-hover>
      </li>
    </ul>
    <div v-if="!loading" class="text-left">
      <v-btn
        v-if="participators?.length > defaultShowedMembersInList"
        variant="text"
        class="text-none text-body-1 pl-1"
        @click="toggleViewMore()"
      >
        <span v-if="!isViewMoreActive">{{ t('components.label_read_more') }}</span>
        <span v-else>{{ t('components.label_read_less') }}</span>
      </v-btn>
    </div>
    <div v-if="loading">
      <v-skeleton-loader class="mt-2" :loading="loading" type="list-item" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.members-list {
  list-style-type: none;

  .v-chip,
  .v-btn {
    border: solid 1px rgba(var(--v-theme-outline-variant));
    border-radius: 4px;
  }
}

:deep(.v-skeleton-loader__list-item) {
  margin: 0;
}

.hidden-tooltip {
  display: none;
}
</style>
